import { useQuery } from '@tanstack/react-query';
import API from '../../../../context/API';
import { useActiveStoreId } from '../../../Stores/hooks';
import { requireActiveStoreId } from '../../../../utils/activeStore';
import { Customization, CustomizationOption, FetchParams, CustomizationsFetchResult } from '../types';

const fetchCustomizationsAPI = async ({ page, search }: FetchParams): Promise<CustomizationsFetchResult> => {
  const storeId = requireActiveStoreId();
  let url = `/customizations?page=${page}&limit=20&storeId=${storeId}`;
  if (search && search.trim()) {
    url += `&search=${encodeURIComponent(search.trim())}`;
  }

  const response = await API.get(url);

  if (!response.data.success) {
    throw new Error('Failed to fetch customizations.');
  }

  const customizationsData: Customization[] = response.data.data.map((customization: any) => ({
    ...customization,
    id: Number(customization.id) || customization.id,
    sequence_number: customization.sequence_number != null ? Number(customization.sequence_number) : undefined,
    option_limit: customization.option_limit,
    sold_out: Boolean(customization.sold_out),
    options: (customization.options || customization.option || [])
      .map((option: any) => ({ ...option, sold_out: Boolean(option.sold_out), picture: option.picture ?? null, image: option.image ?? null }))
      .sort((a: CustomizationOption, b: CustomizationOption) =>
        (a.sequence_number || 0) - (b.sequence_number || 0)
      ),
    option: (customization.option || customization.options || [])
      .map((option: any) => ({ ...option, sold_out: Boolean(option.sold_out), picture: option.picture ?? null, image: option.image ?? null }))
      .sort((a: CustomizationOption, b: CustomizationOption) =>
        (a.sequence_number || 0) - (b.sequence_number || 0)
      ),
    related_items: customization.related_items || [],
  }));

  const sorted = [...customizationsData].sort((a, b) => (b.id as number) - (a.id as number));

  return {
    data: sorted,
    pagination: response.data.pagination,
  };
};

export const CUSTOMIZATIONS_KEY = 'customizations';

export const useCustomizationsQuery = (
  params: FetchParams,
  options?: { enabled?: boolean }
) => {
  const storeId = useActiveStoreId();

  return useQuery({
    queryKey: [CUSTOMIZATIONS_KEY, params.page, params.search ?? '', storeId],
    queryFn: () => fetchCustomizationsAPI(params),
    enabled: options?.enabled !== false && !!storeId,
    staleTime: 30_000,
  });
};
